import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle,
  X,
  PaperPlaneTilt,
  ChatCircleDots,
  CheckCircle,
  Lightning,
  Microphone,
  MicrophoneSlash,
  SpeakerHigh,
  SpeakerSlash,
  CaretDown,
} from "@phosphor-icons/react";
import { askItineraryAssistant } from "../lib/api";
import { useVoice } from "../lib/useVoice";
import { track } from "../lib/analytics";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  "Add a cafe stop on day 1",
  "Make day 2 a bit lighter",
  "Swap the day 3 hotel for something boutique",
  "Add a sunset viewpoint to day 2",
  "Remove the shopping stop",
];

const VOICE_LANGS = [
  { code: "en-IN", label: "English (IN)", flag: "🇮🇳" },
  { code: "en-US", label: "English (US)", flag: "🇺🇸" },
  { code: "en-GB", label: "English (UK)", flag: "🇬🇧" },
  { code: "hi-IN", label: "हिंदी", flag: "🇮🇳" },
  { code: "bn-IN", label: "বাংলা", flag: "🇮🇳" },
  { code: "ta-IN", label: "தமிழ்", flag: "🇮🇳" },
  { code: "te-IN", label: "తెలుగు", flag: "🇮🇳" },
  { code: "mr-IN", label: "मराठी", flag: "🇮🇳" },
  { code: "gu-IN", label: "ગુજરાતી", flag: "🇮🇳" },
  { code: "kn-IN", label: "ಕನ್ನಡ", flag: "🇮🇳" },
];

/**
 * Floating AI Assistant for an existing itinerary.
 * - Sits at bottom-right (above mobile tab bar).
 * - Lets users ask anything OR request edits ("add a cafe on day 1").
 * - On edit, the backend returns the updated itinerary and we call onUpdate(...)
 *   so the parent page re-renders with the new content.
 */
export function ItineraryAssistant({ itineraryId, country, foodPreference, transportPreference, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content, changed? }
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("fl_voice_lang") || "en-IN";
    } catch {
      return "en-IN";
    }
  });
  const [voiceReplyEnabled, setVoiceReplyEnabled] = useState(true);
  const [langOpen, setLangOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem("fl_voice_lang", lang);
    } catch {
      /* noop */
    }
  }, [lang]);

  const send = async (text, sourceLang) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    const useLang = sourceLang || lang;
    setMessages((prev) => [...prev, { role: "user", content: msg, voice: !!sourceLang }]);
    setInput("");
    setBusy(true);
    track("itinerary_assistant_message", {
      country,
      length: msg.length,
      voice: !!sourceLang,
      lang: useLang,
    });
    try {
      const res = await askItineraryAssistant(itineraryId, msg, useLang);
      const reply = res.reply || "Done.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, changed: !!res.changed },
      ]);
      if (res.changed && res.itinerary) {
        onUpdate?.(res.itinerary);
        toast.success("Itinerary updated", {
          description: "Your changes are live on the trip-board.",
        });
        track("itinerary_assistant_edit_applied", { country });
      }
      // Speak the reply if voice mode is on and the user opened the mic on this turn
      if (voiceReplyEnabled && sourceLang) {
        speak(reply, sourceLang);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't reach the AI right now.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: detail, error: true },
      ]);
    } finally {
      setBusy(false);
    }
  };

  // ----- Voice integration --------------------------------------------------
  const {
    supported: voiceSupported,
    listening,
    speaking,
    transcript,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    setTranscript,
  } = useVoice({
    onFinal: (finalText) => {
      // Auto-send the spoken message
      send(finalText, lang);
      setTranscript("");
    },
  });

  // Mirror live transcript into the input as the user speaks
  useEffect(() => {
    if (listening && transcript) setInput(transcript);
  }, [transcript, listening]);

  const toggleMic = () => {
    if (listening) {
      stopListening();
    } else {
      // Cancel any TTS so the recogniser hears the user cleanly
      stopSpeaking();
      track("itinerary_assistant_mic_start", { country, lang });
      startListening(lang);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy, open]);

  // Lock body scroll on mobile while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    if (window.matchMedia("(max-width: 767px)").matches) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            data-testid="itin-assistant-launcher"
            onClick={() => {
              setOpen(true);
              track("itinerary_assistant_opened", { country });
            }}
            className="fixed right-4 sm:right-6 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full font-semibold shadow-2xl shadow-orange-300/50 text-white"
            style={{
              background: "linear-gradient(135deg, #FF7E4D 0%, #ff9466 100%)",
              bottom: "calc(env(safe-area-inset-bottom) + 88px)",
              zIndex: 9500,
            }}
          >
            <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-white/25 backdrop-blur-md">
              <Sparkle size={16} weight="fill" />
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-300 ring-2 ring-[#FF7E4D]" />
            </span>
            <span className="text-sm">Ask AI</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="itin-assistant-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px]"
            style={{ zIndex: 9600 }}
          />
        )}
        {open && (
          <motion.aside
            key="panel"
            initial={{ y: 24, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 340, damping: 28 }}
            data-testid="itin-assistant-panel"
            className="fixed left-3 right-3 sm:left-auto sm:right-6 bottom-3 sm:bottom-6 sm:w-[400px] flex flex-col rounded-3xl bg-white shadow-2xl overflow-hidden"
            style={{
              zIndex: 9700,
              maxHeight: "min(80vh, 640px)",
              bottom: "calc(env(safe-area-inset-bottom) + 80px)",
              border: "1px solid rgba(11,37,69,0.10)",
            }}
          >
            {/* Header */}
            <header
              className="flex items-center justify-between px-5 py-4 text-white"
              style={{
                background:
                  "linear-gradient(135deg, #2A7FB8 0%, #5FB3E3 100%)",
              }}
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full grid place-items-center bg-white/20 backdrop-blur-md relative">
                  <Sparkle size={16} weight="fill" />
                  {speaking && (
                    <span className="absolute inset-0 rounded-full ring-2 ring-white animate-ping" />
                  )}
                </span>
                <div className="leading-tight">
                  <div className="font-serif text-lg flex items-center gap-2 flex-wrap">
                    Trip Co-pilot
                    {foodPreference && foodPreference !== "both" && (
                      <span
                        data-testid="itin-assistant-diet-chip"
                        title={`Diet locked: ${foodPreference}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/25 backdrop-blur-md border border-white/30"
                      >
                        {(() => {
                          const emoji = {
                            veg: "🥗",
                            vegan: "🌱",
                            jain: "🪷",
                            "non-veg": "🍖",
                            halal: "☪️",
                          }[foodPreference] || "🍽";
                          const label = {
                            veg: "Veg",
                            vegan: "Vegan",
                            jain: "Jain",
                            "non-veg": "Non-veg",
                            halal: "Halal",
                          }[foodPreference] || foodPreference;
                          return (
                            <>
                              <span>{emoji}</span>
                              {label}
                            </>
                          );
                        })()}
                      </span>
                    )}
                    {transportPreference && transportPreference !== "mix" && (
                      <span
                        data-testid="itin-assistant-transport-chip"
                        title={`Transport: ${transportPreference}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/25 backdrop-blur-md border border-white/30"
                      >
                        {(() => {
                          const emoji = {
                            bike: "🛵",
                            cab: "🚕",
                            transit: "🚇",
                          }[transportPreference] || "🧭";
                          const label = {
                            bike: "Bike",
                            cab: "Cab",
                            transit: "Transit",
                          }[transportPreference] || transportPreference;
                          return (
                            <>
                              <span>{emoji}</span>
                              {label}
                            </>
                          );
                        })()}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] opacity-90 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                    {listening
                      ? "Listening…"
                      : speaking
                        ? "Speaking…"
                        : "Ask anything · edit on the fly"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {voiceSupported && (
                  <>
                    {/* Language picker */}
                    <div className="relative">
                      <button
                        data-testid="itin-assistant-lang-toggle"
                        onClick={() => setLangOpen((v) => !v)}
                        aria-label="Voice language"
                        className="h-8 px-2.5 rounded-full grid place-items-center hover:bg-white/15 transition-colors text-[11px] font-semibold flex items-center gap-1"
                      >
                        {VOICE_LANGS.find((l) => l.code === lang)?.flag || "🌐"}
                        <span className="hidden sm:inline">
                          {lang.split("-")[0].toUpperCase()}
                        </span>
                        <CaretDown size={10} weight="bold" />
                      </button>
                      <AnimatePresence>
                        {langOpen && (
                          <motion.ul
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            data-testid="itin-assistant-lang-list"
                            className="absolute right-0 top-10 min-w-[180px] rounded-xl bg-white text-slate-800 shadow-2xl overflow-hidden max-h-[260px] overflow-y-auto scroll-thin border border-slate-200"
                            style={{ zIndex: 10 }}
                          >
                            {VOICE_LANGS.map((l) => (
                              <li key={l.code}>
                                <button
                                  data-testid={`itin-assistant-lang-${l.code}`}
                                  onClick={() => {
                                    setLang(l.code);
                                    setLangOpen(false);
                                    stopSpeaking();
                                    stopListening();
                                  }}
                                  className={`w-full text-left px-3.5 py-2.5 text-[13px] flex items-center gap-2 hover:bg-slate-100 ${
                                    l.code === lang
                                      ? "bg-[#EAF4FB] font-semibold text-[#2A7FB8]"
                                      : ""
                                  }`}
                                >
                                  <span>{l.flag}</span>
                                  <span>{l.label}</span>
                                </button>
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </div>
                    {/* Speaker toggle */}
                    <button
                      data-testid="itin-assistant-speaker-toggle"
                      onClick={() => {
                        const next = !voiceReplyEnabled;
                        setVoiceReplyEnabled(next);
                        if (!next) stopSpeaking();
                      }}
                      aria-label={
                        voiceReplyEnabled ? "Mute voice replies" : "Unmute voice replies"
                      }
                      className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/15 transition-colors"
                    >
                      {voiceReplyEnabled ? (
                        <SpeakerHigh size={15} weight="fill" />
                      ) : (
                        <SpeakerSlash size={15} weight="fill" />
                      )}
                    </button>
                  </>
                )}
                <button
                  data-testid="itin-assistant-close"
                  onClick={() => {
                    stopSpeaking();
                    stopListening();
                    setOpen(false);
                  }}
                  aria-label="Close assistant"
                  className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/15 transition-colors"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            </header>

            {/* Body */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-thin bg-[#FDF8EE]/40"
            >
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-2.5">
                    <span className="w-8 h-8 rounded-full grid place-items-center bg-[#EAF4FB] flex-shrink-0">
                      <ChatCircleDots size={16} weight="duotone" color="#2A7FB8" />
                    </span>
                    <div className="text-[13px] text-slate-700 leading-relaxed bg-white border border-slate-200 rounded-2xl rounded-tl-md px-3.5 py-2.5">
                      Hi! I can answer any question about this trip — or just tell me what you&apos;d like to change, and I&apos;ll update the board.
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2 px-1">
                      Quick prompts
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {QUICK_PROMPTS.map((p, i) => (
                        <button
                          key={i}
                          data-testid={`itin-assistant-suggest-${i}`}
                          onClick={() => send(p)}
                          className="text-left text-[13px] text-slate-700 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-[#2A7FB8]/40 hover:bg-[#EAF4FB]/40 transition-colors flex items-start gap-2"
                        >
                          <Lightning size={13} weight="fill" className="text-[#FF7E4D] mt-0.5 flex-shrink-0" />
                          <span>{p}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-2`}
                >
                  {m.role !== "user" && (
                    <span className="w-7 h-7 rounded-full grid place-items-center bg-[#EAF4FB] flex-shrink-0 mt-1">
                      <Sparkle size={13} weight="fill" color="#2A7FB8" />
                    </span>
                  )}
                  <div
                    data-testid={`itin-assistant-msg-${m.role}`}
                    className={`max-w-[82%] text-[13px] leading-relaxed rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap ${
                      m.role === "user"
                        ? "text-white rounded-tr-md"
                        : `${m.error ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-800"} border rounded-tl-md`
                    }`}
                    style={
                      m.role === "user"
                        ? {
                            background:
                              "linear-gradient(135deg, #2A7FB8 0%, #3F95CD 100%)",
                          }
                        : undefined
                    }
                  >
                    {m.content}
                    {m.changed && (
                      <div
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700"
                        data-testid="itin-assistant-changed-badge"
                      >
                        <CheckCircle size={13} weight="fill" />
                        Itinerary updated
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {busy && (
                <div className="flex justify-start gap-2">
                  <span className="w-7 h-7 rounded-full grid place-items-center bg-[#EAF4FB] flex-shrink-0 mt-1">
                    <Sparkle size={13} weight="fill" color="#2A7FB8" />
                  </span>
                  <div
                    data-testid="itin-assistant-typing"
                    className="text-[13px] text-slate-500 border border-slate-200 bg-white rounded-2xl rounded-tl-md px-3.5 py-2.5 flex items-center gap-1.5"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2A7FB8] animate-bounce [animation-delay:-0.3s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2A7FB8] animate-bounce [animation-delay:-0.15s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2A7FB8] animate-bounce" />
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              data-testid="itin-assistant-form"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-slate-200 p-3 bg-white flex items-center gap-2"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}
            >
              {voiceSupported && (
                <button
                  type="button"
                  data-testid="itin-assistant-mic"
                  onClick={toggleMic}
                  aria-label={listening ? "Stop listening" : "Speak"}
                  className={`relative w-11 h-11 rounded-full grid place-items-center transition-all active:scale-95 ${
                    listening
                      ? "text-white"
                      : "text-[#2A7FB8] bg-[#EAF4FB] hover:bg-[#d8ecf7]"
                  }`}
                  style={
                    listening
                      ? {
                          background: "linear-gradient(135deg, #FF7E4D, #ff5a3c)",
                          boxShadow: "0 0 0 4px rgba(255,126,77,0.25)",
                        }
                      : undefined
                  }
                >
                  {listening ? (
                    <>
                      <MicrophoneSlash size={18} weight="fill" />
                      <span className="absolute inset-0 rounded-full ring-2 ring-[#FF7E4D]/60 animate-ping" />
                    </>
                  ) : (
                    <Microphone size={18} weight="fill" />
                  )}
                </button>
              )}
              <input
                data-testid="itin-assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  listening
                    ? "Listening — start speaking…"
                    : busy
                      ? "Working on it…"
                      : "Ask a question or request a change…"
                }
                disabled={busy || listening}
                enterKeyHint="send"
                autoComplete="off"
                className="flex-1 input-base h-11 text-[14px]"
              />
              <button
                type="submit"
                data-testid="itin-assistant-send"
                disabled={busy || listening || !input.trim()}
                className="w-11 h-11 rounded-full grid place-items-center disabled:opacity-40 shadow-md shadow-orange-200/50 transition-transform active:scale-95"
                style={{ background: "#FF7E4D" }}
              >
                <PaperPlaneTilt size={17} color="#fff" weight="fill" />
              </button>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
